/**
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License"). You
 * may not use this file except in compliance with the License. A copy of
 * the License is located at
 *
 *     http://aws.amazon.com/apache2.0/
 *
 * or in the "license" file accompanying this file. This file is
 * distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the License for the specific
 * language governing permissions and limitations under the License.
 */
import { IdleState, BusyState, busyState, TokensFromSignIn, RecoveryReason } from './model';
import { authProxyApi } from '@verifime/auth-proxy-definition';
import { logProxyError, extractProxyErrorCode, summariseErrorForLogging } from './auth-proxy-utils';
import { defaultTokensCb } from 'amazon-cognito-passwordless-auth/common';
import {
  parseJwtPayload,
  currentBrowserLocationWithoutFragmentIdentifier,
  removeFragmentIdentifierFromBrowserLocation,
  bufferFromBase64Url,
} from 'amazon-cognito-passwordless-auth/util';
//import { configure } from 'amazon-cognito-passwordless-auth/config';
import { Passwordless } from 'amazon-cognito-passwordless-auth';
import { CognitoIdTokenPayload } from 'amazon-cognito-passwordless-auth/jwt-model';

const configure = Passwordless.configure;

/**
 * True when a rejection represents an aborted or cancelled request rather
 * than a genuine failure. Checked two ways: the AbortSignal this attempt was
 * given, and the rejection's own shape, since a cancelled axios request (via
 * Zodios) can reject with a CanceledError before the signal's own listeners
 * have run. Axios raises `CanceledError` (name `'CanceledError'`); the
 * fetch/DOM equivalent is `AbortError`. Checked by error name rather than
 * importing axios, matching the structural-check convention already used for
 * proxy errors in this package (see sso.ts's extractActivationStatus).
 */
function isAbortError(err: unknown, signal: AbortSignal): boolean {
  if (signal.aborted) return true;
  const name =
    err && typeof err === 'object' && 'name' in err ? (err as { name?: unknown }).name : undefined;
  return name === 'CanceledError' || name === 'AbortError';
}

/**
 * Classifies a magic-link sign-in failure into the terminal status the caller
 * should land on, plus (where applicable) a recovery-page reason. Returns
 * undefined for anything not recognised as a terminal failure, so the caller
 * rethrows exactly as it does today.
 *
 * A used or expired link deliberately carries no reason (decision D1): the
 * absence of a reason selects the recovery page's default copy, rather than
 * a `'failed'` reason.
 */
export function classifyMagicLinkFailure(
  err: unknown,
): { status: 'SIGNIN_LINK_EXPIRED' | 'INVALID_SIGNIN_LINK'; reason?: RecoveryReason } | undefined {
  const code = extractProxyErrorCode(err);
  const response =
    err && typeof err === 'object' && 'response' in err
      ? (err as { response?: { status?: number } }).response
      : undefined;

  // A used or expired link. MAGIC_LINK_FAILED is never emitted by the
  // deployed proxy today (it maps NotAuthorizedException/UserNotFoundException
  // to AUTH_FAILED instead); it is kept here as a tolerated alias so nothing
  // breaks if the proxy ever starts emitting it.
  //
  // Do not "fix" this by asking the proxy for a magic-link-specific code. It
  // cannot produce one usefully, and the reason is a security control rather
  // than an oversight. The magic-link verifier already distinguishes six
  // failure reasons and collapses them to a bare false, which Cognito then
  // reports as a generic NotAuthorizedException, so the detail never reaches
  // the proxy at all. Separately, telling a used link apart from an unknown
  // user would rebuild the enumeration oracle that pentest finding M2 was
  // remediated to remove: preventUserExistenceErrors makes those two cases
  // indistinguishable on purpose. And POST /auth/respond already carries
  // method: 'MAGIC_LINK' from us, so AUTH_FAILED on this route has exactly one
  // meaning. Handling it entirely client side, as below, is the decision.
  // Full write-up: vault note "Why the auth proxy cannot emit MAGIC_LINK_FAILED".
  if (
    code === 'AUTH_FAILED' ||
    code === 'INVALID_CHALLENGE_RESPONSE' ||
    code === 'SESSION_EXPIRED' ||
    code === 'INVALID_SESSION' ||
    code === 'MAGIC_LINK_FAILED'
  ) {
    return { status: 'SIGNIN_LINK_EXPIRED' };
  }

  // A malformed magic-link fragment.
  if (code === 'INVALID_REQUEST') {
    return { status: 'INVALID_SIGNIN_LINK', reason: 'broken-link' };
  }

  // Our fault, not a dead link: an unmapped proxy error, any 5xx status, or a
  // network error that never reached the proxy at all (no response object,
  // indistinguishable from a 500 at this layer) are all deliberately treated
  // the same way.
  if (
    code === 'INTERNAL_ERROR' ||
    !response ||
    (typeof response.status === 'number' && response.status >= 500)
  ) {
    return { status: 'INVALID_SIGNIN_LINK', reason: 'server-error' };
  }

  return undefined;
}

export const requestSignInLink = ({
  username,
  oneTimePassword,
  redirectUri,
  currentStatus,
  statusCb,
}: {
  /**
   * Username, or alias (e-mail, phone number)
   */
  username: string;
  oneTimePassword: string;
  redirectUri?: string;
  currentStatus?: BusyState | IdleState;
  statusCb?: (status: BusyState | IdleState) => void;
}) => {
  const { debug } = configure();
  if (currentStatus && busyState.includes(currentStatus as BusyState)) {
    throw new Error(`Can't request sign-in link while in status ${currentStatus}`);
  }
  statusCb?.('REQUESTING_SIGNIN_LINK');
  const abort = new AbortController();
  const signInLinkRequested = (async (): Promise<undefined> => {
    try {
      // otp and redirectUri are sent in the request body; signInMethod and
      // alreadyHaveMagicLink are handled server-side. Empty string is not a
      // valid portal ID; treat as absent.
      const portal = process.env.NEXT_PUBLIC_AUTH_PORTAL_ID || undefined;

      debug?.('Invoking auth proxy initiateAuth for magic link ...');
      const initResponse = await authProxyApi.postAuthinitiate(
        {
          email: username,
          method: 'MAGIC_LINK',
          portal,
          otp: oneTimePassword,
          redirectUri: redirectUri || currentBrowserLocationWithoutFragmentIdentifier(),
        },
        { signal: abort.signal },
      );
      debug?.('Response from auth proxy initiateAuth:', initResponse);

      if (initResponse.challengeType !== 'MAGIC_LINK_SENT') {
        throw new Error('Expected MAGIC_LINK_SENT from auth proxy');
      }

      // The proxy holds the Cognito session; nothing to store here.
      statusCb?.('SIGNIN_LINK_REQUESTED');
      return undefined;
    } catch (err) {
      logProxyError('Magic link request failed', err);
      debug?.(err);
      currentStatus && statusCb?.('SIGNIN_LINK_REQUEST_FAILED');
      throw err;
    }
  })();
  return {
    signInLinkRequested,
    abort: () => abort.abort(),
  };
};

const failedFragmentIdentifieres = new Set<string>();

/**
 * Test-only: clears the module-level record of fragment identifiers already
 * known to have failed. Production code never calls this; the set is
 * intentionally permanent for the page's lifetime so a credential already
 * known dead is never re-attempted against the proxy (see
 * `failedFragmentIdentifieres`'s use in `signInWithLink`). Exported purely
 * so tests can isolate cases explicitly rather than relying on each test
 * happening to use a distinct `exp` offset.
 */
export function __resetFailedFragmentIdentifiersForTesting(): void {
  failedFragmentIdentifieres.clear();
}

function checkCurrentLocationForSignInLink() {
  const { debug } = configure();
  let url: URL;
  let fragmentIdentifier: string;
  try {
    url = new URL(window.location?.href);
    fragmentIdentifier = url.hash?.slice(1);
    if (!fragmentIdentifier) {
      debug?.('Current location.href has no fragment identifier, nothing to do');
      return;
    }
    if (failedFragmentIdentifieres.has(fragmentIdentifier)) {
      debug?.('Current location.href has a fragment identifier that failed before, ignoring');
      return;
    }
  } catch (e) {
    // TODO Implement DeepLinks for React Native MagicLink Support
    debug?.("Couldn't parse location url");
    return;
  }
  const header = fragmentIdentifier.split('.')[0];
  let message: unknown;
  try {
    debug?.('Parsing magic link header:', header);
    message = JSON.parse(new TextDecoder().decode(bufferFromBase64Url(header)));
    debug?.('Magic link header parsed:', message);
    assertIsMessage(message);
  } catch (err) {
    debug?.('Ignoring invalid fragment identifier');
    return;
  }
  if (!message.userName || typeof message.userName !== 'string') {
    debug?.(`Ignoring fragment identifier with invalid username:`, message.userName);
    return;
  }
  if (!message.exp || typeof message.exp !== 'number') {
    debug?.(`Ignoring fragment identifier with invalid exp:`, message.userName);
    return;
  }
  return {
    username: message.userName,
    exp: message.exp,
    fragmentIdentifier,
  };
}

function assertIsMessage(
  msg: unknown,
): asserts msg is { userName: string; exp: number; iat: number } {
  if (
    !msg ||
    typeof msg !== 'object' ||
    !('userName' in msg) ||
    typeof msg.userName !== 'string' ||
    !('exp' in msg) ||
    typeof msg.exp !== 'number' ||
    !('iat' in msg) ||
    typeof msg.iat !== 'number'
  ) {
    throw new Error('Invalid magic link');
  }
}

async function authenticateWithSignInLink({
  username,
  fragmentIdentifier,
  currentStatus,
  abort,
}: {
  /**
   * Username, or alias (e-mail, phone number)
   */
  username: string;
  fragmentIdentifier: string;
  currentStatus?: BusyState | IdleState;
  abort?: AbortSignal;
}): Promise<TokensFromSignIn> {
  const { debug } = configure();
  if (currentStatus && busyState.includes(currentStatus as BusyState)) {
    throw new Error(`Can't authenticate with link while in status ${currentStatus}`);
  }

  // Auth proxy path: no session needed. The proxy creates a fresh Cognito
  // session from the fragment's embedded username. signInMethod, redirectUri
  // and alreadyHaveMagicLink metadata are handled server-side by the proxy.
  const portal = process.env.NEXT_PUBLIC_AUTH_PORTAL_ID || undefined;

  // Yield once before contacting the proxy. The removed direct-Cognito
  // stale-session cleanup (an `await storage.removeItem(...)`) incidentally
  // provided this: React StrictMode's mount -> cleanup -> mount happens
  // synchronously, so without a yield point here, a discarded first mount's
  // request to redeem the (single-use) magic link would actually be
  // dispatched before its effect's cleanup has a chance to call abort() on
  // it, rather than being pre-empted. See
  // PasswordlessLogin.integration.test.tsx's StrictMode abort-race test.
  await Promise.resolve();

  try {
    debug?.('Invoking auth proxy respondToChallenge for magic link ...');
    const authResult = await authProxyApi.postAuthrespond(
      {
        method: 'MAGIC_LINK',
        portal,
        answer: { fragment: fragmentIdentifier } as Record<string, unknown>,
      },
      { signal: abort },
    );
    debug?.('Response from auth proxy respondToChallenge:', authResult);

    return {
      accessToken: authResult.accessToken,
      idToken: authResult.idToken,
      refreshToken: authResult.refreshToken,
      expireAt: new Date(Date.now() + authResult.expiresIn * 1000),
      username: parseJwtPayload<CognitoIdTokenPayload>(authResult.idToken)['cognito:username'],
    };
  } catch (err) {
    logProxyError('Magic link verification failed', err);
    throw err;
  }
}

export const signInWithLink = (props?: {
  tokensCb?: (tokens: TokensFromSignIn) => void | Promise<void>;
  statusCb?: (status: BusyState | IdleState, reason?: RecoveryReason, expiresAt?: Date) => void;
  /**
   * When true, a detected-but-still-valid link is not consumed automatically:
   * `signedIn` resolves once 'SIGNIN_LINK_PENDING_CONFIRMATION' is emitted
   * (carrying the link's expiry as its third statusCb argument), and the
   * returned `confirm()` performs the consumption on demand -- exactly the
   * same code the non-deferred path below runs inline, including the abort
   * handling and the failedFragmentIdentifieres bookkeeping. `confirm` is
   * only present on the returned object when there is a still-valid link to
   * confirm; otherwise (no link, or an already-expired one) it is undefined.
   *
   * Without this option, behaviour is unchanged: a valid, unexpired link is
   * consumed the moment this function runs, as today.
   */
  deferConsumption?: boolean;
}) => {
  const { debug } = configure();
  const abort = new AbortController();
  const { statusCb, tokensCb, deferConsumption } = props ?? {};

  /**
   * The consumption step: everything that happens once a still-valid link is
   * actually redeemed. Shared verbatim by the non-deferred path (called
   * inline below) and by the deferred path's exposed confirm() (called on
   * demand, once per press) -- there is exactly one implementation of "spend
   * the link", not two copies to keep in sync.
   *
   * A second call after a failed first call is a deliberate retry, not a
   * duplicate: it is not guarded by failedFragmentIdentifieres, so a press
   * that failed (e.g. a transient network error) can be pressed again and
   * reach the proxy afresh. That module-level set still gets written to on
   * failure (below), and still guards the *detection* step above against a
   * passive re-mount (React StrictMode, a page reload) re-attempting a
   * credential already known dead -- it just no longer gates an explicit,
   * user-initiated retry of the same in-memory attempt.
   */
  async function consume(validParams: {
    username: string;
    fragmentIdentifier: string;
  }): Promise<TokensFromSignIn | undefined> {
    const { username, fragmentIdentifier } = validParams;
    statusCb?.('SIGNING_IN_WITH_LINK');
    try {
      const tokens = await authenticateWithSignInLink({
        username,
        fragmentIdentifier,
        abort: abort.signal,
      }).catch((err) => {
        if (isAbortError(err, abort.signal)) {
          // The caller unmounted (e.g. React StrictMode's double-invoked
          // mount effect) or a newer attempt superseded this one before the
          // proxy responded. Not a failure to report: emit no terminal
          // status and leave the fragment usable, so the surviving attempt
          // is the one that decides the outcome.
          debug?.(
            'Magic-link sign-in attempt aborted, emitting no terminal status',
            summariseErrorForLogging(err),
          );
          return;
        }
        const classified = classifyMagicLinkFailure(err);
        if (!classified) throw err;
        debug?.(err);
        // This early return skips the outer catch below, so this branch owns
        // recording the fragment as dead. Without this, a re-mount (e.g.
        // React StrictMode) would re-attempt a credential already known to
        // be dead against the proxy.
        failedFragmentIdentifieres.add(fragmentIdentifier);
        // Reason is undefined for a used or expired link (decision D1); the
        // consumer treats an absent reason as the default recovery copy.
        statusCb?.(classified.status, classified.reason);
        return;
      });
      if (!tokens) return undefined;
      removeFragmentIdentifierFromBrowserLocation();
      tokensCb ? await tokensCb(tokens) : await defaultTokensCb({ tokens, abort: abort.signal });
      statusCb?.('SIGNED_IN_WITH_LINK');
      return tokens;
    } catch (err) {
      if (isAbortError(err, abort.signal)) {
        // Same reasoning as the inner catch above: an aborted/superseded
        // attempt is not a failure to report.
        debug?.(
          'Magic-link sign-in attempt aborted, emitting no terminal status',
          summariseErrorForLogging(err),
        );
        return undefined;
      }
      failedFragmentIdentifieres.add(fragmentIdentifier);
      // This outer catch is reached either by an unclassified proxy failure
      // (classifyMagicLinkFailure rethrew rather than classifying it) or by
      // a failure after authentication already succeeded (token storage via
      // tokensCb/defaultTokensCb, or fragment cleanup via
      // removeFragmentIdentifierFromBrowserLocation), neither of which is
      // the classifier's job to recognise. Either way it is our fault, not
      // the customer's link being old. Reason server-error keeps it off the
      // default "link is no longer valid" copy that an absent reason
      // selects under decision D1.
      statusCb?.('INVALID_SIGNIN_LINK', 'server-error');
      throw err;
    }
  }

  // Detection is synchronous and side-effect free (beyond the
  // failedFragmentIdentifieres read), so it runs eagerly, before signedIn's
  // async chain, letting confirm() below close over an already-validated
  // params without an unnecessary re-check.
  const params = checkCurrentLocationForSignInLink();
  const isExpired = !!params && params.exp < Date.now() / 1000;

  const signedIn = (async () => {
    if (!params) {
      statusCb?.('NO_SIGNIN_LINK');
      return;
    }
    if (isExpired) {
      // No reason emitted here (decision D1): this lands on the same default
      // recovery copy as a proxy-classified expired/used link.
      statusCb?.('SIGNIN_LINK_EXPIRED');
      return;
    }
    if (deferConsumption) {
      statusCb?.('SIGNIN_LINK_PENDING_CONFIRMATION', undefined, new Date(params.exp * 1000));
      return;
    }
    return consume(params);
  })();

  return {
    signedIn,
    abort: () => abort.abort(),
    // Only present when there is a still-valid link deferred for
    // confirmation: nothing to confirm otherwise (no link, or an expired
    // one), and the non-deferred path already consumed it inline above.
    ...(deferConsumption && params && !isExpired ? { confirm: () => consume(params) } : {}),
  };
};
